import React, { useState, useEffect, useRef } from 'react';
import { PlayCircle, BookOpen, Video, ExternalLink, Key, Calendar, Star, ExternalLink as LinkIcon, Sparkles } from 'lucide-react';

// Custom hook for scroll animation
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(element);
    return () => element && observer.unobserve(element);
  }, [threshold]);

  return [ref, isVisible];
};

const VideoLessonsSection = () => {
  const [sampleLessonRef, sampleLessonVisible] = useScrollAnimation(0.1);

  // NEW: Real Sample Lesson - Number Sequences & Nth Term
  const sampleLesson = {
    id: "sample-lesson",
    title: "📚 LIVE Lesson: Number Sequences & Nth Term",
    description: "Watch a REAL lesson with Dr. Michael teaching GCSE students about arithmetic sequences, geometric sequences, the Fibonacci sequence, and how to find the nth term formula. See his interactive teaching style in action!",
    videoUrl: "https://customer-assets.emergentagent.com/job_gcsemath/artifacts/u1oztf5u_Screen_Recording_20260118_220828_Chrome.mp4",
    duration: "~10 mins",
    topics: ["Arithmetic Sequences (+2 pattern)", "Nth Term Formula (2n+1)", "Geometric Sequences (×2 pattern)", "Fibonacci Sequence"],
    isNew: true
  };

  // NEW: Second Sample Lesson - Completing the Square & Perpendicular Lines
  const sampleLesson2 = {
    id: "sample-lesson-2",
    title: "📚 LIVE Lesson: Completing the Square & Perpendicular Lines",
    description: "Watch Dr. Michael working 1-on-1 with students on Grade 8-9 topics including completing the square and finding the equation of perpendicular lines and tangents. Real tutoring in action!",
    videoUrl: "https://customer-assets.emergentagent.com/job_tuition-hub-17/artifacts/cjng7j51_GMT20260122-162001_Recording_2880x1800.mp4",
    duration: "Full session",
    topics: ["Completing the Square", "Perpendicular Lines", "Equation of a Tangent", "Higher Tier Topics"],
    isNew: true
  };

  // Main highlighted video - Most Important
  const mainHighlightedVideo = {
    id: "main-highlight",
    title: "GCSE Maths Mastery 🎓",
    description: "Watch Dr. Michael Omakobia's comprehensive teaching approach that has helped countless students achieve top grades in GCSE Maths.",
    videoId: "0x84c_FSdQw",
    type: "youtube"
  };

  // THREE KEYS TO SUCCESS VIDEO - Very Important!
  const revisionKeysVideo = {
    id: "revision-keys",
    title: "3 Essential Revision Keys to Pass GCSE Maths 🔑",
    description: "Discover the THREE most important revision strategies that every GCSE Maths student MUST know to maximise their grades. These proven techniques have helped countless students achieve their target grades.",
    videoId: "dPBoymsJ6yk",
    type: "youtube"
  };

  // Secondary featured video
  const secondaryFeaturedVideo = {
    id: "featured-youtube",
    title: "Dividing Fractions RAP! 🎤",
    description: "Dr. Michael's unique teaching style - using rap to make maths memorable and fun!",
    videoId: "lFuanoeKGxo",
    type: "youtube"
  };

  // TikTok highlight - shown as a card, not embedded
  const tiktokHighlight = {
    id: "featured-tiktok",
    title: "Quick Maths Tips on TikTok 🎯",
    description: "Follow Dr. Michael for bite-sized maths tips and engaging explainers!",
    tiktokUrl: "https://vm.tiktok.com/ZNR6TA237/",
    profileUrl: "https://www.tiktok.com/@drmichaelomakobia"
  };

  const lessons = [
    {
      id: 1,
      title: "HCF and LCM Explained 📐",
      description: "Learn what Highest Common Factor (HCF) and Lowest Common Multiple (LCM) are - essential concepts for GCSE Maths success",
      videoId: "r3UO03sydNI"
    },
    {
      id: 2,
      title: "Multi-Step Time Problem ⏱️",
      description: "Master solving multi-step GCSE Maths problems involving time - a common exam question type",
      videoId: "5MnKp5Qwaq8"
    },
    {
      id: 3,
      title: "Compound Measures: Pressure",
      description: "IGCSE Maths Chapter 9 - Master the compound measure of pressure with clear explanations",
      videoId: "nNWFoANoyGY"
    },
    {
      id: 4,
      title: "Compound Measures: Density",
      description: "Essential GCSE Maths concepts explained by Dr. Michael Omakobia",
      videoId: "LGxdNu5MF1c"
    },
    {
      id: 5,
      title: "Compound Measures: Speed",
      description: "Step-by-step guidance through key GCSE Maths topics",
      videoId: "fKYtvi3pDds"
    }
  ];

  return (
    <section id="video-lessons" style={{ 
      padding: '5rem 1.5rem', 
      backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.97), rgba(248, 250, 252, 0.97)), url('https://static.prod-images.emergentagent.com/jobs/ee1a1db2-a588-42e0-b44d-7c9f207c1d4b/images/ffde3c3c5f9b03ea798fe77ab2447bff2c488b9e79e11e4956101bcaa3b064e5.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: 'var(--accent-wash)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Video size={28} color="var(--accent-text)" />
            </div>
          </div>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Free Video Lessons
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
            Watch Dr. Michael Omakobia's teaching in action. Get a taste of his engaging teaching style and proven methods that help students excel.
          </p>
        </div>

        {/* NEW: Real Sample Lesson - Number Sequences */}
        <div 
          ref={sampleLessonRef}
          style={{ 
            marginBottom: '4rem', 
            maxWidth: '1000px', 
            margin: '0 auto 4rem',
            opacity: sampleLessonVisible ? 1 : 0,
            transform: sampleLessonVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(135deg, #C5A059, #C5A059)',
              color: 'white',
              padding: '12px 28px',
              borderRadius: '9999px',
              fontSize: '1rem',
              fontWeight: 700,
              boxShadow: '0 4px 20px rgba(20, 184, 166, 0.4)',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              <Sparkles size={20} />
              🔥 NEW: Watch a REAL Lesson!
              <Sparkles size={20} />
            </div>
          </div>

          <div style={{
            background: 'white',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            border: '3px solid #C5A059'
          }}>
            {/* Video Player */}
            <div style={{ 
              position: 'relative', 
              paddingBottom: '56.25%', 
              height: 0, 
              background: '#000' 
            }}>
              <video
                controls
                poster=""
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
              >
                <source src={sampleLesson.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div style={{ padding: '2rem' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '1rem', 
                marginBottom: '1rem',
                flexWrap: 'wrap'
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 800, 
                  color: 'var(--text-primary)',
                  margin: 0
                }}>
                  {sampleLesson.title}
                </h3>
                <span style={{
                  background: '#C5A059',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 700
                }}>
                  NEW
                </span>
                <span style={{
                  background: 'var(--accent-wash)',
                  color: 'var(--accent-text)',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  fontSize: '0.8rem',
                  fontWeight: 600
                }}>
                  {sampleLesson.duration}
                </span>
              </div>

              <p style={{ 
                color: 'var(--text-secondary)', 
                marginBottom: '1.5rem',
                fontSize: '1rem',
                lineHeight: 1.6
              }}>
                {sampleLesson.description}
              </p>

              {/* Topics Covered */}
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ 
                  fontWeight: 700, 
                  color: 'var(--text-primary)', 
                  marginBottom: '0.75rem',
                  fontSize: '0.9rem'
                }}>
                  📖 Topics Covered in This Lesson:
                </p>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '0.5rem' 
                }}>
                  {sampleLesson.topics.map((topic, index) => (
                    <span 
                      key={index}
                      style={{
                        background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                        border: '1px solid rgba(20, 184, 166, 0.3)',
                        color: '#C5A059',
                        padding: '6px 14px',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        fontWeight: 500
                      }}
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 213, 79, 0.15), rgba(255, 213, 79, 0.05))',
                border: '1px solid rgba(255, 213, 79, 0.4)',
                borderRadius: '12px',
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '1.5rem' }}>💡</span>
                <span style={{ color: '#0F172A', fontWeight: 600, fontSize: '0.9rem' }}>
                  This is an actual lesson recording showing Dr. Michael's interactive teaching style with real students!
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Second Sample Lesson - Completing the Square & Perpendicular Lines */}
        <div 
          ref={sampleLessonRef}
          style={{ 
            marginBottom: '3rem', 
            maxWidth: '900px', 
            margin: '0 auto 3rem',
            opacity: sampleLessonVisible ? 1 : 0,
            transform: sampleLessonVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.6s ease-out 0.1s'
          }}
        >
          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #C5A059, #D4AF61)',
              color: '#0F172A',
              padding: '10px 28px',
              borderRadius: '9999px',
              fontSize: '0.9rem',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(197, 160, 89, 0.4)'
            }}>
              🎓 LIVE 1-on-1 Session - Higher Tier Topics
            </div>
          </div>

          <div className="product-card" style={{ 
            padding: '0', 
            overflow: 'hidden',
            border: '3px solid #C5A059'
          }}>
            {/* Video Player */}
            <div style={{ 
              position: 'relative', 
              paddingBottom: '56.25%', 
              height: 0, 
              background: '#000' 
            }}>
              <video
                controls
                poster=""
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
              >
                <source src={sampleLesson2.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div style={{ padding: '2rem' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '1rem', 
                marginBottom: '1rem',
                flexWrap: 'wrap'
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 800, 
                  color: 'var(--text-primary)',
                  margin: 0
                }}>
                  {sampleLesson2.title}
                </h3>
                <span style={{
                  background: '#C5A059',
                  color: '#0F172A',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 700
                }}>
                  NEW
                </span>
                <span style={{
                  background: 'rgba(197, 160, 89, 0.15)',
                  color: '#0F172A',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  fontSize: '0.8rem',
                  fontWeight: 600
                }}>
                  {sampleLesson2.duration}
                </span>
              </div>

              <p style={{ 
                color: 'var(--text-secondary)', 
                marginBottom: '1.5rem',
                fontSize: '1rem',
                lineHeight: 1.6
              }}>
                {sampleLesson2.description}
              </p>

              {/* Topics Covered */}
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ 
                  fontWeight: 700, 
                  color: 'var(--text-primary)', 
                  marginBottom: '0.75rem',
                  fontSize: '0.9rem'
                }}>
                  📖 Topics Covered in This Session:
                </p>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '0.5rem' 
                }}>
                  {sampleLesson2.topics.map((topic, index) => (
                    <span 
                      key={index}
                      style={{
                        background: 'linear-gradient(135deg, rgba(197, 160, 89, 0.15), rgba(197, 160, 89, 0.05))',
                        border: '1px solid rgba(197, 160, 89, 0.4)',
                        color: '#0F172A',
                        padding: '6px 14px',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        fontWeight: 500
                      }}
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, rgba(197, 160, 89, 0.15), rgba(197, 160, 89, 0.05))',
                border: '1px solid rgba(197, 160, 89, 0.4)',
                borderRadius: '12px',
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '1.5rem' }}>🎯</span>
                <span style={{ color: '#0F172A', fontWeight: 600, fontSize: '0.9rem' }}>
                  Watch a real 1-on-1 tutoring session tackling Grade 8-9 topics - perfect for Higher Tier students!
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Highlighted Video - Full Width */}
        <div style={{ marginBottom: '3rem', maxWidth: '900px', margin: '0 auto 3rem' }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              display: 'inline-block',
              background: 'linear-gradient(to right, #C5A059, #A88B4A)',
              color: 'white',
              padding: '10px 28px',
              borderRadius: '9999px',
              fontSize: '0.9rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(255, 0, 0, 0.3)'
            }}>
              ⭐ Featured Video - Must Watch!
            </div>
          </div>

          <div className="product-card" style={{ 
            padding: '0', 
            overflow: 'hidden',
            border: '4px solid #C5A059',
            boxShadow: '0 8px 32px rgba(255, 0, 0, 0.2)'
          }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, background: '#000' }}>
              <iframe
                src={`https://www.youtube.com/embed/${mainHighlightedVideo.videoId}`}
                title={mainHighlightedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
              ></iframe>
            </div>
            <div style={{ 
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(255, 0, 0, 0.05) 0%, rgba(255, 0, 0, 0.1) 100%)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                {mainHighlightedVideo.title}
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                {mainHighlightedVideo.description}
              </p>
            </div>
          </div>
        </div>

        {/* THREE KEYS TO REVISION SUCCESS - Important Section */}
        <div style={{ 
          marginBottom: '3rem',
          background: 'linear-gradient(135deg, rgba(64, 224, 208, 0.06) 0%, rgba(255, 213, 79, 0.06) 100%)',
          borderRadius: '20px',
          padding: '2rem 1.5rem'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(to right, var(--accent-primary), #C5A059)',
              color: 'white',
              padding: '10px 28px',
              borderRadius: '9999px',
              fontSize: '0.9rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(64, 224, 208, 0.4)'
            }}>
              🔑 3 Keys to GCSE Success
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            maxWidth: '1100px',
            margin: '0 auto',
            alignItems: 'start'
          }}>
            {/* Video Card */}
            <div className="product-card" style={{ 
              padding: '0', 
              overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(64, 224, 208, 0.15)'
            }}>
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, background: '#000' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${revisionKeysVideo.videoId}`}
                  title={revisionKeysVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                ></iframe>
              </div>
              <div style={{ 
                padding: '1.5rem',
                background: 'var(--accent-wash)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '0.5rem', color: 'var(--accent-text)' }}>
                  {revisionKeysVideo.title}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-body)', margin: 0 }}>
                  {revisionKeysVideo.description}
                </p>
              </div>
            </div>

            {/* Maths Genie Recommendation Card */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Recommendation Box */}
              <div className="product-card" style={{ 
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(255, 213, 79, 0.15) 0%, rgba(255, 193, 7, 0.2) 100%)',
                boxShadow: '0 4px 16px rgba(255, 193, 7, 0.15)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'var(--accent-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Star size={24} color="white" fill="white" />
                  </div>
                  <div>
                    <h4 style={{ 
                      fontSize: '1.1rem', 
                      fontWeight: 700, 
                      color: 'var(--accent-secondary-text)',
                      margin: 0
                    }}>
                      Dr. Michael's Top Recommendation
                    </h4>
                  </div>
                </div>
                
                <p className="body-medium" style={{ 
                  color: 'var(--text-body)', 
                  marginBottom: '1rem',
                  lineHeight: 1.6
                }}>
                  I <strong>highly recommend</strong> <a 
                    href="https://www.mathsgenie.co.uk" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: 'var(--accent-text)', fontWeight: 600 }}
                  >www.mathsgenie.co.uk</a> as an excellent all-round GCSE revision resource. It's the <strong>perfect revision tool</strong> to help your child maximise their grades!
                </p>

                <a 
                  href="https://www.mathsgenie.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: 'var(--accent-secondary)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textDecoration: 'none',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 193, 7, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <BookOpen size={18} />
                  Visit Maths Genie
                  <ExternalLink size={14} />
                </a>
              </div>

              {/* Free Coaching Call CTA */}
              <div className="product-card" style={{ 
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(64, 224, 208, 0.12) 0%, rgba(64, 224, 208, 0.18) 100%)',
                textAlign: 'center',
                boxShadow: '0 4px 16px rgba(64, 224, 208, 0.15)'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: 'var(--accent-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <Calendar size={28} color="white" />
                </div>
                
                <h4 style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: 700, 
                  color: 'var(--text-primary)',
                  marginBottom: '0.75rem'
                }}>
                  Parents: Book a Free Coaching Call
                </h4>
                
                <p className="body-small" style={{ 
                  color: 'var(--text-secondary)', 
                  marginBottom: '1.25rem',
                  lineHeight: 1.5
                }}>
                  Let me show you exactly how your child can use Maths Genie and other resources as the <strong>perfect revision tool</strong> to achieve their best possible grades.
                </p>

                <a 
                  href="https://calendly.com/drmomakobia/studentcall"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textDecoration: 'none',
                    width: '100%',
                    justifyContent: 'center'
                  }}
                >
                  <Calendar size={18} />
                  Schedule Free Call
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* SPEED CHALLENGE - Special Offer Section */}
        <div style={{ 
          marginBottom: '3rem',
          maxWidth: '900px',
          margin: '0 auto 3rem'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(to right, #9333EA, #0F172A)',
              color: 'white',
              padding: '10px 28px',
              borderRadius: '9999px',
              fontSize: '0.9rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(147, 51, 234, 0.4)'
            }}>
              🏆 Speed Challenge - Win 10% Lifetime Discount!
            </div>
          </div>

          <div className="product-card" style={{ 
            padding: '0', 
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(147, 51, 234, 0.2)'
          }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, background: '#000' }}>
              <iframe
                src="https://www.youtube.com/embed/lVRo7v7-QoA"
                title="Speed = Distance ÷ Time Challenge"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
              ></iframe>
            </div>
            <div style={{ 
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.08) 0%, rgba(124, 58, 237, 0.12) 100%)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: '#0F172A' }}>
                Speed = Distance ÷ Time 🚀
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-body)', marginBottom: '1rem' }}>
                Master the compound measure of speed using the essential formula. Watch the video and test your understanding with the questions!
              </p>
              
              {/* Special Offer Banner */}
              <div style={{
                background: 'linear-gradient(135deg, #9333EA 0%, #0F172A 100%)',
                color: 'white',
                padding: '1rem 1.25rem',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  flexShrink: 0
                }}>
                  🎁
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                    Score 100% = 10% Lifetime Discount!
                  </div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                    Any student who scores 100% on the video questions receives a 10% lifetime discount off the Monthly Package!
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* COMPOUND INTEREST CARTOON - Creative Learning Section */}
        <div style={{ 
          marginBottom: '3rem',
          maxWidth: '900px',
          margin: '0 auto 3rem'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(to right, #A88B4A, #C5A059)',
              color: 'white',
              padding: '10px 28px',
              borderRadius: '9999px',
              fontSize: '0.9rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.4)'
            }}>
              🎬 Cartoon Learning - Percentages & Savings!
            </div>
          </div>

          <div className="product-card" style={{ 
            padding: '0', 
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(5, 150, 105, 0.2)'
          }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, background: '#000' }}>
              <iframe
                src="https://www.youtube.com/embed/j6mUBjBzelo"
                title="Percentages & Compound Interest - Cartoon Style"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
              ></iframe>
            </div>
            <div style={{ 
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.08) 0%, rgba(16, 185, 129, 0.12) 100%)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: '#A88B4A' }}>
                Percentages & The Power of Compound Interest 💰
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-body)', marginBottom: '1rem' }}>
                Learn how to work with percentages in this creative cartoon-style lesson! Discover the amazing power of saving money in a compound interest savings account and watch your money grow.
              </p>
              
              {/* Key Learning Points */}
              <div style={{
                background: 'linear-gradient(135deg, #A88B4A 0%, #C5A059 100%)',
                color: 'white',
                padding: '1rem 1.25rem',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  flexShrink: 0
                }}>
                  📈
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                    Dr. Omakobia's Cartoon Style Teaching
                  </div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                    A fun and engaging way to master percentages and understand how compound interest can make your savings grow over time!
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BUDGETING CARTOON - Salary & Savings Story */}
        <div style={{ 
          marginBottom: '3rem',
          maxWidth: '900px',
          margin: '0 auto 3rem'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(to right, #C5A059, #E5C575)',
              color: 'white',
              padding: '10px 28px',
              borderRadius: '9999px',
              fontSize: '0.9rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(236, 72, 153, 0.4)'
            }}>
              👗 Cartoon Story - Budgeting & Saving for a Goal!
            </div>
          </div>

          <div className="product-card" style={{ 
            padding: '0', 
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(236, 72, 153, 0.2)'
          }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, background: '#000' }}>
              <iframe
                src="https://www.youtube.com/embed/MvkH6F_FnEE"
                title="Budgeting & Salary Calculations - Cartoon Style"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
              ></iframe>
            </div>
            <div style={{ 
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, rgba(244, 114, 182, 0.12) 100%)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: '#C5A059' }}>
                Budgeting, Salary & Savings - Who Will Buy the Dress? 👗💸
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-body)', marginBottom: '1rem' }}>
                Follow Oluwafemi's McDonald's job journey as he calculates his annual salary after tax, National Insurance, and living expenses to save for a dress for Anjali. Meanwhile, Matthew uses the power of compound interest and the "Bank of Mum & Dad" - who will reach their goal first?
              </p>
              
              {/* Key Topics Banner */}
              <div style={{
                background: 'linear-gradient(135deg, #C5A059 0%, #E5C575 100%)',
                color: 'white',
                padding: '1rem 1.25rem',
                borderRadius: '12px'
              }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>📚</span> GCSE Topics Covered:
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '0.5rem',
                  fontSize: '0.85rem'
                }}>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '9999px' }}>Annual Salary</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '9999px' }}>Tax Deductions</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '9999px' }}>National Insurance</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '9999px' }}>Budgeting</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '9999px' }}>Compound Interest</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '9999px' }}>Living Expenses</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* INSPIRATIONAL VIDEO - Stay in School & Never Give Up */}
        <div style={{ 
          marginBottom: '3rem',
          maxWidth: '900px',
          margin: '0 auto 3rem'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(to right, #C5A059, #C5A059)',
              color: 'white',
              padding: '10px 28px',
              borderRadius: '9999px',
              fontSize: '0.9rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)'
            }}>
              ⭐ Inspiration - Never Give Up!
            </div>
          </div>

          <div className="product-card" style={{ 
            padding: '0', 
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(245, 158, 11, 0.2)'
          }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, background: '#000' }}>
              <iframe
                src="https://www.youtube.com/embed/ryB8RCYi3Rg"
                title="Stay in School & Never Give Up"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
              ></iframe>
            </div>
            <div style={{ 
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(234, 179, 8, 0.12) 100%)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: '#D97706' }}>
                Stay in School, Never Give Up & Surround Yourself with Success! 🌟
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-body)', marginBottom: '1rem' }}>
                An important message from Dr. Michael about the power of education, perseverance, and the incredible impact of surrounding yourself with like-minded friends who share your ambition to achieve high grades.
              </p>
              
              {/* Key Messages */}
              <div style={{
                background: 'linear-gradient(135deg, #C5A059 0%, #C5A059 100%)',
                color: 'white',
                padding: '1rem 1.25rem',
                borderRadius: '12px'
              }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>💪</span> Key Messages:
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '0.5rem',
                  fontSize: '0.85rem'
                }}>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: '9999px' }}>📚 Stay in School</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: '9999px' }}>💪 Never Give Up</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: '9999px' }}>👥 Form Friendship Groups</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: '9999px' }}>🎯 Aim for High Grades</span>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: '9999px' }}>🌟 Believe in Yourself</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Featured Section - Two Columns */}
        <div style={{
          marginBottom: '3rem'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              display: 'inline-block',
              background: 'linear-gradient(to right, var(--accent-secondary), var(--accent-secondary-strong))',
              color: 'white',
              padding: '8px 24px',
              borderRadius: '9999px',
              fontSize: '0.875rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(255, 193, 7, 0.4)'
            }}>
              🎤 More from Dr. Michael
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '1.5rem',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {/* Secondary YouTube Video */}
            <div className="product-card" style={{ 
              padding: '0', 
              overflow: 'hidden',
              border: '2px solid var(--accent-secondary)'
            }}>
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, background: '#000' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${secondaryFeaturedVideo.videoId}`}
                  title={secondaryFeaturedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                ></iframe>
              </div>
              <div style={{ padding: '1.25rem' }}>
                <div style={{ 
                  display: 'inline-block',
                  background: '#C5A059',
                  color: 'white',
                  padding: '3px 10px',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  marginBottom: '0.5rem'
                }}>
                  YouTube
                </div>
                <h3 className="heading-3" style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                  {secondaryFeaturedVideo.title}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                  {secondaryFeaturedVideo.description}
                </p>
              </div>
            </div>

            {/* TikTok Card - Compact Horizontal Design */}
            <a
              href={tiktokHighlight.tiktokUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="product-card"
              style={{ 
                padding: '0', 
                overflow: 'hidden',
                border: '2px solid #000',
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* TikTok Visual Header */}
              <div style={{
                background: 'linear-gradient(135deg, #000000 0%, #C5A059 50%, #A88B4A 100%)',
                padding: '2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '180px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="white" style={{ marginBottom: '0.75rem' }}>
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                  <div style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
                    @drmichaelomakobia
                  </div>
                </div>
              </div>
              
              {/* TikTok Info */}
              <div style={{ padding: '1.25rem', flex: 1 }}>
                <div style={{ 
                  display: 'inline-block',
                  background: '#000',
                  color: 'white',
                  padding: '3px 10px',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  marginBottom: '0.5rem'
                }}>
                  TikTok
                </div>
                <h3 className="heading-3" style={{ marginBottom: '0.5rem', fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                  {tiktokHighlight.title}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  {tiktokHighlight.description}
                </p>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#A88B4A',
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}>
                  <PlayCircle size={18} />
                  Watch on TikTok
                  <ExternalLink size={14} />
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* More Lessons Heading */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h3 className="heading-3" style={{ color: 'var(--text-primary)' }}>
            More Video Lessons
          </h3>
          <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
            Additional lessons covering key GCSE Maths topics
          </p>
        </div>

        {/* Video Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1.5rem',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {lessons.map((lesson) => (
            <div key={lesson.id} className="product-card" style={{ padding: '0', overflow: 'hidden' }}>
              {/* Video Embed */}
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, background: '#000' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${lesson.videoId}`}
                  title={lesson.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                ></iframe>
              </div>

              {/* Video Info */}
              <div style={{ padding: '1.25rem' }}>
                <h3 className="heading-3" style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>
                  {lesson.title}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                  {lesson.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Box */}
        <div style={{
          marginTop: '3rem',
          maxWidth: '800px',
          margin: '3rem auto 0',
          padding: '2rem',
          background: 'linear-gradient(135deg, var(--accent-wash) 0%, rgba(255, 213, 79, 0.1) 100%)',
          border: '2px solid var(--accent-primary)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <PlayCircle size={48} color="var(--accent-text)" style={{ margin: '0 auto 1rem' }} />
          <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
            Want More Personalized Learning?
          </h3>
          <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
            These free lessons are just a preview. Get personalized attention, exam-focused strategies, and expert guidance tailored to your needs.
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary"
          >
            Book Your Session Now
          </button>
        </div>

        {/* YouTube Channel Link */}
        <div style={{ textAlign: 'center', marginTop: '2rem', display: 'flex', gap: '2rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a 
            href="https://www.youtube.com/@DrMichaelOmakobia"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'var(--accent-text)',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'opacity 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.opacity = 0.7}
            onMouseLeave={(e) => e.target.style.opacity = 1}
          >
            <BookOpen size={20} />
            Subscribe on YouTube
          </a>
          
          <a 
            href="https://www.tiktok.com/@drmichaelomakobia"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#A88B4A',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'opacity 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.opacity = 0.7}
            onMouseLeave={(e) => e.target.style.opacity = 1}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
            Follow on TikTok
          </a>
        </div>
      </div>
    </section>
  );
};

export default VideoLessonsSection;
